import featureGuideStyles from '../../../shared/src/ui/featureGuides.css?inline';
import trialExitQuestionStyles from '../../../shared/src/ui/trialExitQuestion.css?inline';
import { FOUNDATIONS, PALETTE } from '@overlayqa/shared';
/**
 * Shadow DOM CSS for the content script overlay.
 *
 * This CSS is injected into the Shadow DOM to style the PersistentPopup
 * and all overlay components. It provides Tailwind-like utility classes
 * scoped to the shadow root so they cannot leak into the host page.
 *
 * Colors are aligned with tailwind.config.js.
 */

import { createDebug } from '../utils/debug';

const debug = createDebug('OverlayStyles');

/**
 * Overlay layers that render as DIRECT CHILDREN of #overlayqa-overlay-container
 * and must not be hit-testable.
 *
 * WHY THIS LIST EXISTS AT ALL. The container's `> *` rule below re-enables
 * pointer events on every direct child at ID specificity (1,0,0), so it
 * outranks ANY class rule (0,1,0 / 0,2,0). A layer that declares
 * `pointer-events: none` in its BEM block is therefore silently overridden:
 * nothing throws, nothing logs, and the declaration still greps as present.
 * "Declared none in the BEM sheet" and "is actually click-through" are
 * different claims, and only an ID-scoped rule makes the second one true.
 *
 * Adding a page layer? Add its class here — the CSS is generated from this
 * array, so the rule and the list cannot drift. Two guards read it:
 *   - src/content/__tests__/overlayContainerClickThrough.test.ts (source, in `pnpm gate`)
 *   - e2e/verify-overlay-layer-click-through.ts (real Chromium, the authority)
 *
 * Measured 2026-08-14: `.oq-crop` and `.oq-highlight--simple-preview` both
 * computed `auto` here while their BEM rules said `none`, and a page button
 * under a saved crop region was unreachable. Same class as the 2026-08-13
 * churn ("it will block me from leaving this page"), which had only ever been
 * fixed for `.oq-highlight--inert`.
 */
export const CLICK_THROUGH_LAYER_CLASSES = [
  /** Passive element-pin highlight. The pin marker is the sole opener.
   *  2026-08-13 churn P0; see _highlight.css. */
  'oq-highlight--inert',
  /** Saved + live screenshot-region rectangles (_pin.css). A saved crop covers
   *  an ARBITRARY rectangle of the page for the pin's whole life, in the
   *  passive state, and carries no data-* attribute — so neither
   *  isOverlayUITarget nor useHoverPreview recognises it and nothing gains
   *  from it being hit-testable. The armed drag is owned by `.oq-canvas`
   *  (position: fixed; inset: 0), not by this rectangle. */
  'oq-crop',
  /** Armed-mode hover preview (_highlight.css). It is drawn AT the cursor's
   *  own element, so a hit-testable preview sits under the cursor that drew
   *  it. */
  'oq-highlight--simple-preview',
  /** Figma overlay frame while locked (_overlay.css). Long-standing; folded in
   *  here 2026-08-14 so every ID-scoped opt-out has one home. */
  'oq-overlay--locked',
  /** The armed capture canvas while capture is PAUSED (_canvas.css).
   *
   *  The MODIFIER, never `oq-canvas` itself: the base class is one of the
   *  three layers the blanket rule above exists for, and opting it out would
   *  leave capture mode unable to receive a click at all. Pausing is the
   *  user's own choice, made in the toolbar and undone in the same control.
   *
   *  This is the unfixed half of the 2026-08-13 churn. The four entries above
   *  cover the PASSIVE state, so once the fix landed a user with pins on the
   *  page could click through them; with capture ARMED the canvas still
   *  covered the viewport and the only route back to the page was ending the
   *  session. The reporter wanted to file issues across several layers of one
   *  URL and could not drive their app between them. */
  'oq-canvas--paused',
  /** The two tooltip bubbles, both portaled into the container as DIRECT
   *  children (components/shared/Tooltip.tsx and the shared
   *  mountToolbarTooltips in @overlayqa/shared/ui). Each declares
   *  `pointer-events: none` in its own sheet, and the blanket rule above
   *  defeated both: the bubble's arrow then sat under the pointer that opened
   *  it, the mounter read that as leaving the trigger and hid the bubble, and
   *  the pointer was back on the trigger, so a tooltip on a 12px trigger (the
   *  compare row's related-findings icon) flashed for 14ms every 400ms and
   *  never settled. Measured in the extension 2026-10-19 (ordered event log in
   *  docs/test-reports/2026-10-19-compare-related-findings-icon/); the large
   *  toolbar and rail targets hid the same defeat for months because their
   *  centre never sat under the arrow. */
  'oq-tooltip',
  'oq-capture-tooltip',
] as const;

const CLICK_THROUGH_RULES = CLICK_THROUGH_LAYER_CLASSES.map(
  (className) => `  #overlayqa-overlay-container > .${className} {\n    pointer-events: none;\n  }`,
).join('\n');

export const OVERLAY_CSS = featureGuideStyles + trialExitQuestionStyles + `
  /* ============================================
     Shadow DOM CSS for PersistentPopup
     Colors aligned with tailwind.config.js
     ============================================ */

  /* Container setup */
  #overlayqa-overlay-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    z-index: 2147483647;
    pointer-events: none;
  }
  /* Load-bearing: the sidebar, the panels and the ARMED capture canvas are all
     direct children and must stay hit-testable. Deleting this would "fix" the
     click-through bug by breaking the whole overlay. */
  #overlayqa-overlay-container > * {
    pointer-events: auto;
  }

  /* ...except the passive page layers, which are opted out at ID specificity
     because a class rule (0,1,0) cannot outrank the ID rule above (1,0,0).
     Generated from CLICK_THROUGH_LAYER_CLASSES so the list and the CSS cannot
     drift; the reasoning per layer lives on that array. Measured in real
     Chromium, not read off the stylesheet -- a defeated pointer-events:none
     looks identical to a working one in the source. */
${CLICK_THROUGH_RULES}

  /* Base styles (matches popup/styles.css) */
  /* IMPORTANT: Set explicit font-size and color to prevent host page interference.
     all: initial on the host element resets color to CanvasText, which can vary
     across pages and rendering contexts. !important overrides the inline style. */
  :host {
    font-size: ${FOUNDATIONS.fontSize.heading} !important;
    color: ${PALETTE.neutral[900]} !important;
  }

  * {
    font-family: ${FOUNDATIONS.fontFamily.system};
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    font-size: inherit;
    line-height: ${FOUNDATIONS.lineHeight.body};
  }

  /* Migrated workflow and saved-issue reading lines follow the approved grid. */
  :where(.oq-workflow-ui, .oq-workflow-ui *, .oq-project-issues, .oq-project-issues *, .oq-project-tabs, .oq-project-tabs *, .oq-created-issues-ui, .oq-created-issues-ui *) { line-height: round(nearest, 1.5em, var(--oq-space-1)); }

  /* Button reset - remove browser defaults */
  button {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    color: inherit;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    margin: 0;
  }

  /* Form element resets */
  input, select, textarea {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  :where(.oq-workflow-ui, .oq-project-issues, .oq-project-tabs, .oq-created-issues-ui) :is(button, input, select, textarea) { line-height: round(nearest, 1.5em, var(--oq-space-1)); }

  /* Range slider and checkbox accent colors (scaled up) */
  input[type="range"] {
    accent-color: var(--oq-sky-600);
    height: 0.5rem;
  }

  input[type="checkbox"]:not(.oq-checkbox) {
    accent-color: var(--oq-sky-600);
    width: 1.125rem;
    height: 1.125rem;
  }

  /* ============================================
     LAYOUT UTILITIES (scaled up gaps ~10-15%)
     ============================================ */
  .flex { display: flex; }
  .flex-col { flex-direction: column; }
  .flex-1 { flex: 1 1 0%; }
  .items-center { align-items: center; }
  .items-start { align-items: flex-start; }
  .justify-between { justify-content: space-between; }
  .justify-center { justify-content: center; }
  .gap-1 { gap: 0.375rem; }
  .gap-2 { gap: 0.625rem; }
  .gap-3 { gap: 0.875rem; }
  .gap-4 { gap: 1.125rem; }
  .min-w-0 { min-width: 0; }

  /* ============================================
     SIZING UTILITIES (scaled up ~10-15%)
     ============================================ */
  .w-2 { width: 0.5rem; }
  .w-4 { width: 1.125rem; }
  .w-6 { width: 1.625rem; }
  .w-8 { width: 2.25rem; }
  .w-12 { width: 3.25rem; }
  .w-16 { width: 4.5rem; }
  .w-full { width: 100%; }
  .h-1_5 { height: 0.5rem; }
  .h-2 { height: 0.625rem; }
  .h-4 { height: 1.125rem; }
  .h-6 { height: 1.625rem; }
  .h-8 { height: 2.25rem; }
  .h-12 { height: 3.25rem; }
  .h-16 { height: 4.5rem; }
  .h-20 { height: 5.5rem; }
  .h-32 { height: 9rem; }
  .h-full { height: 100%; }
  .max-w-\\[140px\\] { max-width: 160px; }
  .max-w-\\[150px\\] { max-width: 170px; }
  .max-h-48 { max-height: 13.5rem; }
  .max-h-64 { max-height: 18rem; }

  /* ============================================
     SPACING UTILITIES (scaled up ~10-15%)
     ============================================ */
  .p-1\\.5 { padding: 0.5rem; }
  .p-3 { padding: 0.875rem; }
  .p-4 { padding: 1.125rem; }
  .p-8 { padding: 2.25rem; }
  .px-2 { padding-left: 0.625rem; padding-right: 0.625rem; }
  .px-3 { padding-left: 0.875rem; padding-right: 0.875rem; }
  .px-4 { padding-left: 1.125rem; padding-right: 1.125rem; }
  .px-9 { padding-left: 2.5rem; padding-right: 2.5rem; }
  .pl-9 { padding-left: 2.5rem; }
  .pr-3 { padding-right: 0.875rem; }
  .py-0\\.5 { padding-top: 0.1875rem; padding-bottom: 0.1875rem; }
  .py-1\\.5 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
  .py-2 { padding-top: 0.625rem; padding-bottom: 0.625rem; }
  .py-3 { padding-top: 0.875rem; padding-bottom: 0.875rem; }
  .py-4 { padding-top: 1.125rem; padding-bottom: 1.125rem; }
  .py-6 { padding-top: 1.75rem; padding-bottom: 1.75rem; }
  .py-12 { padding-top: 3.5rem; padding-bottom: 3.5rem; }
  .px-1\\.5 { padding-left: 0.5rem; padding-right: 0.5rem; }
  .mt-1 { margin-top: 0.375rem; }
  .mt-3 { margin-top: 0.875rem; }
  .mb-1 { margin-bottom: 0.375rem; }
  .mb-2 { margin-bottom: 0.625rem; }
  .mb-3 { margin-bottom: 0.875rem; }
  .pt-3 { padding-top: 0.875rem; }
  .mx-auto { margin-left: auto; margin-right: auto; }
  .space-y-2 > * + * { margin-top: 0.625rem; }
  .space-y-3 > * + * { margin-top: 0.875rem; }
  .space-y-4 > * + * { margin-top: 1.125rem; }

  /* ============================================
     POSITIONING UTILITIES
     ============================================ */
  .relative { position: relative; }
  .absolute { position: absolute; }
  .fixed { position: fixed; }
  .inset-0 { top: 0; right: 0; bottom: 0; left: 0; }
  .top-1\\/2 { top: 50%; }
  .left-3 { left: 0.75rem; }
  .z-10 { z-index: 10; }
  .-translate-y-1\\/2 { transform: translateY(-50%); }

  /* ============================================
     TYPOGRAPHY UTILITIES (scaled up one level)
     Original: xs=12px, sm=14px, base=16px, lg=18px
     Scaled:   xs=14px, sm=16px, base=18px, lg=20px
     ============================================ */
  .text-xs { font-size: 0.875rem; line-height: 1.25rem; }
  .text-sm { font-size: 1rem; line-height: 1.5rem; }
  .text-base { font-size: 1.125rem; line-height: 1.75rem; }
  .text-lg { font-size: 1.25rem; line-height: 1.75rem; }
  .font-medium { font-weight: 500; }
  .font-semibold { font-weight: 600; }
  .font-bold { font-weight: 700; }
  .text-left { text-align: left; }
  .text-center { text-align: center; }
  .truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

  /* ============================================
     PRIMARY COLORS (from tailwind.config.js - sky/cyan)
     ============================================ */
  .bg-primary-50 { background-color: var(--oq-sky-50); }
  .bg-primary-100 { background-color: var(--oq-sky-100); }
  .bg-primary-600 { background-color: var(--oq-sky-600); }
  .text-primary-600 { color: var(--oq-sky-600); }
  .text-primary-700 { color: var(--oq-sky-700); }
  .border-primary-200 { border-color: var(--oq-sky-200); }
  .border-primary-500 { border-color: var(--oq-sky-500); }
  .border-primary-600 { border-color: var(--oq-sky-600); }
  .accent-primary-600 { accent-color: var(--oq-sky-600); }

  /* ============================================
     GRAY COLORS
     ============================================ */
  .bg-white { background-color: var(--oq-white); }
  .bg-gray-50 { background-color: var(--oq-gray-50); }
  .bg-gray-100 { background-color: var(--oq-gray-100); }
  .bg-gray-200 { background-color: var(--oq-gray-200); }
  .text-white { color: var(--oq-white); }
  .text-gray-400 { color: var(--oq-gray-400); }
  .text-gray-500 { color: var(--oq-gray-500); }
  .text-gray-600 { color: var(--oq-gray-600); }
  .text-gray-700 { color: var(--oq-gray-700); }
  .text-gray-900 { color: var(--oq-gray-900); }
  .border-gray-100 { border-color: var(--oq-gray-100); }
  .border-gray-200 { border-color: var(--oq-gray-200); }
  .border-gray-300 { border-color: var(--oq-gray-300); }

  /* ============================================
     SEMANTIC COLORS
     ============================================ */
  /* Green (success) */
  .bg-green-500 { background-color: var(--oq-success); }
  .text-green-600 { color: var(--oq-success-dark); }

  /* Red (error/danger) */
  .bg-red-100 { background-color: var(--oq-red-100); }
  .bg-red-600 { background-color: var(--oq-error); }
  .text-red-600 { color: var(--oq-error); }

  /* Orange (warning) */
  .bg-orange-50 { background-color: var(--oq-orange-50); }
  .bg-orange-100 { background-color: var(--oq-orange-100); }
  .text-orange-600 { color: var(--oq-orange-600); }
  .text-orange-700 { color: var(--oq-orange-700); }
  .border-orange-200 { border-color: var(--oq-orange-200); }

  /* Yellow (minor) */
  .bg-yellow-100 { background-color: var(--oq-yellow-100); }
  .text-yellow-600 { color: var(--oq-yellow-600); }

  /* Amber (warning box) */
  .bg-amber-50 { background-color: var(--oq-amber-50); }
  .border-amber-200 { border-color: var(--oq-amber-200); }
  .text-amber-600 { color: var(--oq-warning-dark); }
  .text-amber-800 { color: var(--oq-amber-800); }

  /* Blue (for some buttons) */
  .bg-blue-600 { background-color: var(--oq-blue-600); }
  .text-blue-600 { color: var(--oq-blue-600); }

  /* ============================================
     BORDER UTILITIES (scaled up radii)
     ============================================ */
  .border { border-width: 1px; border-style: solid; border-color: var(--oq-gray-200); }
  .border-2 { border-width: 2px; }
  .border-b { border-bottom-width: 1px; }
  .border-t { border-top-width: 1px; }
  .border-r { border-right-width: 1px; border-right-style: solid; border-right-color: var(--oq-gray-200); }
  .border-dashed { border-style: solid; }
  .border-t-transparent { border-top-color: transparent; }
  .rounded { border-radius: 0.375rem; }
  .rounded-lg { border-radius: 0.625rem; }
  .rounded-full { border-radius: 9999px; }

  /* ============================================
     DISPLAY & OVERFLOW UTILITIES
     ============================================ */
  .block { display: block; }
  .hidden { display: none; }
  .overflow-auto { overflow: auto; }
  .overflow-hidden { overflow: hidden; }
  .overflow-y-auto { overflow-y: auto; }

  /* ============================================
     FORM UTILITIES
     ============================================ */
  .resize-none { resize: none; }
  .appearance-none { appearance: none; }
  .cursor-pointer { cursor: pointer; }
  .object-cover { object-fit: cover; }

  /* ============================================
     TRANSITION UTILITIES
     ============================================ */
  .transition-colors {
    transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: var(--oq-motion-feedback, 150ms);
  }

  /* ============================================
     HOVER STATES
     ============================================ */
  .hover\\:bg-gray-50:hover { background-color: var(--oq-gray-50); }
  .hover\\:bg-gray-100:hover { background-color: var(--oq-gray-100); }
  .hover\\:bg-gray-200:hover { background-color: var(--oq-gray-200); }
  .hover\\:bg-primary-100:hover { background-color: var(--oq-sky-100); }
  .hover\\:bg-primary-700:hover { background-color: var(--oq-sky-700); }
  .hover\\:bg-red-700:hover { background-color: var(--oq-error-dark); }
  .hover\\:bg-blue-700:hover { background-color: var(--oq-blue-700); }
  .hover\\:text-gray-700:hover { color: var(--oq-gray-700); }
  .hover\\:text-primary-600:hover { color: var(--oq-sky-600); }
  .hover\\:border-primary-500:hover { border-color: var(--oq-sky-500); }
  .hover\\:underline:hover { text-decoration: underline; }

  /* ============================================
     FOCUS STATES
     ============================================ */
  .focus\\:ring-2:focus {
    --tw-ring-offset-shadow: 0 0 #0000;
    --tw-ring-shadow: 0 0 0 2px var(--tw-ring-color, var(--oq-sky-500));
    box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), 0 0 #0000;
    outline: none;
  }
  .focus\\:ring-primary-500:focus { --tw-ring-color: var(--oq-sky-500); }
  .focus\\:border-primary-500:focus { border-color: var(--oq-sky-500); }

  /* ============================================
     DISABLED STATES
     ============================================ */
  .disabled\\:opacity-50:disabled { opacity: 0.5; }

  /* ============================================
     GROUP HOVER (for parent-child hover effects)
     ============================================ */
  .group:hover .group-hover\\:bg-primary-100 { background-color: var(--oq-sky-100); }
  .group:hover .group-hover\\:text-primary-600 { color: var(--oq-sky-600); }

  /* ============================================
     ANIMATIONS
     ============================================ */
  .animate-spin { animation: spin 1s linear infinite; }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* ============================================
     SVG UTILITIES
     ============================================ */
  svg { display: inline-block; vertical-align: middle; }
`;

debug.log('Overlay styles module loaded');
